import { useMemo } from 'react';
import {
  Box,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import styles from './aiDashboard.module.scss';
import PieChart from './components/PieChart';
import LineBarChart from './components/LineBarChart';
import { FilterSelect, PageActionButton } from 'components/Styled';
import Icon from 'components/FormControl/Icon';
import useDateRangePicker from 'components/PageDatePicker/useDateRangePicker';
import useFilter from './hooks/useFilter';
import { dateRangeType } from 'components/PageDatePicker/types';
import dayjs from 'dayjs';
import {
  BAR_VIEW_TYPE,
  ExpiredItemsURL,
  ExpiryItemsURL,
  LowStockURL,
  ExpiryItemColumns,
  LowStockColumns,
  StaffMetricsColumns,
  TopSKUColumns,
  UNIT_TYPE,
  PIE_VIEW_TYPE,
} from './constants';
import Label from 'components/Common/Label';
import DashboardHeader from './components/Header';
import DashboardTable from './components/Table';
import useDashboard from './useDashboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Import MUI icon
import ExpandedTableModal from './components/ExpandedTableModal';
import BackDropLoader from 'pages/Wrappers/BackDropLoader';

const initialPieData = { labels: [], datasets: [] };

const AIDashboard = () => {
  const { openHandler, closeHandler, DateRangePopOver } = useDateRangePicker();
  const { dateRange, barViewType, tab, unitType, pieViewType } = useFilter();

  const handleApplyFilter = (filter: dateRangeType[]) => {
    dateRange.set(filter);
    closeHandler();
  };

  const {
    headerData,
    topSellingSku,
    footfall,
    staffMetrics,
    bills,
    expiredItem,
    expiryItem,
    lowStockItem,
    isPolling,
    modalOpen,
    modalData,
    demographics,
    handleCloseModal,
    handleShowMore,
    generatePieData,
    generateChartData,
  } = useDashboard({
    from: dayjs(dateRange.get[0].startDate).startOf('day').unix(),
    to: dayjs(dateRange.get[0].endDate).endOf('day').unix(),
  });

  // Age Pie Data
  const agePieData = useMemo(
    () =>
      demographics?.length > 0
        ? generatePieData(pieViewType.get === 'Category' ? 'category' : 'age')
        : initialPieData,
    [demographics, pieViewType.get],
  );

  // Sales  Pie Data
  const salesPieData = useMemo(
    () => (bills.length > 0 ? generatePieData('sales') : initialPieData),
    [bills],
  );

  // Customer Pie Data
  const customerPieData = useMemo(
    () => (bills.length > 0 ? generatePieData('customer') : initialPieData),
    [bills],
  );

  const chartData = useMemo(
    () => generateChartData(barViewType.get === 'Footfall & Sales' ? 'time' : 'day'),
    [bills, footfall, barViewType.get],
  );

  // const topSellingSkuPieData = useMemo(
  //   () => (topSellingSku.length > 0 ? generatePieData('topSku') : initialPieData),
  //   [topSellingSku],
  // );

  const footfallDay = useMemo(
    () => footfall.reduce((acc, { personCount }) => acc + personCount, 0),
    [footfall],
  );

  return (
    <Box className={styles.pageWrapper}>
      <BackDropLoader loading={isPolling} className={styles.backdropLoader}>
        <Box className={styles.pageContent}>
          {/* Date Range */}
          <Grid container gap={2} px={1} mt={2}>
            <Grid item>
              <FilterSelect
                items={Object.values(UNIT_TYPE())}
                label='Units'
                value={unitType.get}
                variant='standard'
                onChange={(e) => unitType.set(e.target.value as string)}
                minWidth={100}
                maxWidth={150}
              />
            </Grid>
            <Grid item marginLeft={'auto'}>
              <PageActionButton
                variant='outlined'
                text={dateRange.title}
                startIcon={<Icon icon='ri-calendar-2-line' size='1.2rem' />}
                onClick={openHandler}
              />
              <DateRangePopOver
                disableFuture
                dateRange={dateRange.get}
                apply={handleApplyFilter}
                customStaticRanges={[
                  'Today',
                  'Yesterday',
                  'Last 7 Days',
                  'Last 30 Days',
                  'This Month',
                  'Current Quarter',
                  'Current Financial Year',
                ]}
              />
            </Grid>
          </Grid>

          {/* Header */}
          <DashboardHeader headerData={headerData} />

          <Stack direction='row' alignItems='center' spacing={2} px={1} mt={2}>
            {/* FF VS Sales */}
            <Box className={styles.lineBarChart}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                px={2}
                pt={2}
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Label
                    title='FF Count'
                    subtitle={footfallDay ?? 0}
                    titleClassName={styles.customTitle}
                    subTitleClassName={styles.customSubTitle}
                  />
                  <Divider orientation='vertical' sx={{ borderRightWidth: '2px' }} flexItem />
                  <Label
                    title='Sales Count'
                    subtitle={bills.length ?? 0}
                    titleClassName={styles.customTitle}
                    subTitleClassName={styles.customSubTitle}
                  />
                </Stack>
                <FilterSelect
                  items={Object.values(BAR_VIEW_TYPE())}
                  label='View By'
                  value={barViewType.get}
                  variant='standard'
                  onChange={(e) => barViewType.set(e.target.value as string)}
                  minWidth={100}
                  maxWidth={150}
                />
              </Stack>
              <Box height={400}>
                <LineBarChart
                  title={
                    barViewType.get === 'Footfall & Sales'
                      ? 'Footfall and Sales Overview'
                      : 'Days of Weeks'
                  }
                  chartData={chartData}
                />
              </Box>
            </Box>
            {/* NOTE: The pie chart for age demographics is commented out. Will uncomment when get demographics data. */}
            {/* Split by Age */}
            <Box className={styles.pieChart}>
              <Stack direction='row' justifyContent='flex-end' px={2} pt={2}>
                <FilterSelect
                  items={Object.values(PIE_VIEW_TYPE())}
                  label='View By'
                  value={pieViewType.get}
                  variant='standard'
                  onChange={(e) => pieViewType.set(e.target.value as string)}
                  minWidth={100}
                  maxWidth={150}
                />
              </Stack>
              <Box height={400}>
                <PieChart
                  title={
                    pieViewType.get === 'Age Group'
                      ? 'Split By Age Group Chart'
                      : 'Split By Age Category Chart'
                  }
                  pieData={agePieData}
                  unit={unitType.get}
                  hideLabel={pieViewType.get == 'Age Group'}
                />
              </Box>
            </Box>
            {/* <Box className={styles.pieChart} height={460}>
              <PieChart
                title={'Top Selling SKU'}
                pieData={topSellingSkuPieData}
                unit={unitType.get}
              />
            </Box> */}
          </Stack>

          <Stack direction={'row'} alignItems={'center'} spacing={2} px={1} mt={2}>
            {/* Staff Metrics  */}
            <Box width={'60%'} className={styles.card} bgcolor={'#fff'}>
              <Typography
                variant='h6'
                textAlign={'center'}
                className={styles.tableTitle}
                color={'#fff'}
                py={1}
              >
                Staff Performance Metrics
              </Typography>
              <Box height={320} overflow={'auto'}>
                <DashboardTable data={staffMetrics.slice(0, 5)} columns={StaffMetricsColumns} />
              </Box>
              <Box textAlign='center'>
                <Tooltip title={'Show More'}>
                  <IconButton
                    color='primary'
                    onClick={() =>
                      handleShowMore({
                        title: 'Staff Performance Metrics',
                        columns: StaffMetricsColumns,
                        data: staffMetrics,
                      })
                    }
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {/* Top  Selling  Sku  */}
            <Box width={'40%'} className={styles.card} bgcolor={'#fff'}>
              <Typography
                variant='h6'
                textAlign={'center'}
                className={styles.tableTitle}
                color={'#fff'}
                py={1}
              >
                Top Selling SKUs
              </Typography>
              <Box height={320} overflow={'auto'}>
                <DashboardTable columns={TopSKUColumns} data={topSellingSku.slice(0, 5)} />
              </Box>
              <Box textAlign='center'>
                <Tooltip title={'Show More'}>
                  <IconButton
                    color='primary'
                    onClick={() =>
                      handleShowMore({
                        title: 'Top Selling SKUs',
                        columns: TopSKUColumns,
                        data: topSellingSku,
                      })
                    }
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Stack>
          <Stack direction='row' spacing={2} px={1} mt={2}>
            {/* Repeat Vs New Customer */}
            <Box width={'30%'} className={styles.card} height={385}>
              <PieChart
                pieData={customerPieData}
                title={'Repeat Vs New Customer'}
                unit={unitType.get}
              />
            </Box>
            {/* Delivery Vs Store Sales */}
            <Box width={'30%'} className={styles.card} height={385}>
              <PieChart
                pieData={salesPieData}
                title={'Delivery Vs Store Sales'}
                unit={unitType.get}
              />
            </Box>
            {/* Expiry,Expired,Out Of Stock */}
            <Box width={'40%'} className={styles.card} bgcolor={'#fff'}>
              <Paper elevation={1}>
                <Tabs
                  value={tab.get}
                  onChange={(_, value) => tab.set(value)}
                  variant='scrollable'
                  orientation='horizontal'
                >
                  <Tab value={0} label='Expiring Items' />
                  <Tab value={1} label='Expired Items' />
                  <Tab value={2} label='Out Of Stock' />
                </Tabs>
                <Box height={300} overflow={'auto'}>
                  <DashboardTable
                    columns={
                      tab.get === 0
                        ? ExpiryItemColumns
                        : tab.get === 1
                        ? ExpiryItemColumns
                        : LowStockColumns
                    }
                    data={
                      tab.get === 0
                        ? expiryItem.slice(0, 5)
                        : tab.get === 1
                        ? expiredItem.slice(0, 5)
                        : lowStockItem.slice(0, 5)
                    }
                  />
                </Box>
                <Box textAlign='center'>
                  <Tooltip title={'Show More'}>
                    <IconButton
                      color='primary'
                      onClick={() =>
                        handleShowMore({
                          title:
                            tab.get === 0
                              ? 'Expiring Items'
                              : tab.get === 1
                              ? 'Expired Items'
                              : 'Out of Stock',
                          columns:
                            tab.get === 0 || tab.get === 1 ? ExpiryItemColumns : LowStockColumns,
                          data:
                            tab.get === 0 ? expiryItem : tab.get === 1 ? expiredItem : lowStockItem,
                          redirectUrl:
                            tab.get === 0
                              ? ExpiryItemsURL
                              : tab.get === 1
                              ? ExpiredItemsURL
                              : LowStockURL,
                        })
                      }
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Box>
          </Stack>

          <ExpandedTableModal
            open={modalOpen}
            onClose={handleCloseModal}
            title={modalData.title}
            rows={modalData.data}
            columns={modalData.columns}
            redirectUrl={modalData.redirectUrl}
          />
        </Box>
      </BackDropLoader>
    </Box>
  );
};

export default AIDashboard;
